/**
 * Utility functions for persisting table state in URL parameters
 * Supports sorting, search, pagination, and column visibility
 */

// Debounce utility to prevent excessive URL updates
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Serialize table state to URL-safe format
export const serializeTableState = (state) => {
  const urlState = {};
  
  // Serialize sort model
  if (state.sortModel && state.sortModel.length > 0) {
    urlState.sort = state.sortModel.map(sort => `${sort.field}:${sort.sort}`).join(',');
  }
  
  // Serialize search (trim to remove leading/trailing whitespace)
  if (state.search && state.search.trim()) {
    urlState.search = state.search.trim();
  }
  
  // Serialize pagination
  if (state.pagination) {
    if (state.pagination.page > 0) {
      urlState.page = state.pagination.page.toString();
    }
    if (state.pagination.pageSize !== 10) { // Only include if not default
      urlState.pageSize = state.pagination.pageSize.toString();
    }
  }
  
  // Column visibility persistence removed as requested
  
  return urlState;
};

// Deserialize URL parameters back to table state
export const deserializeTableState = (urlParams) => {
  const state = {};
  
  // Deserialize sort model
  const sortParam = urlParams.get('sort');
  if (sortParam) {
    try {
      state.sortModel = sortParam.split(',').map(sortStr => {
        const [field, sort] = sortStr.split(':');
        return { field, sort };
      });
    } catch (error) {
      console.warn('Failed to parse sort parameter:', error);
      state.sortModel = [];
    }
  } else {
    state.sortModel = [];
  }
  
  // Deserialize search (trim to handle any whitespace)
  const searchParam = urlParams.get('search');
  if (searchParam) {
    state.search = decodeURIComponent(searchParam).trim();
  } else {
    state.search = '';
  }
  
  // Deserialize pagination
  const pageParam = urlParams.get('page');
  const pageSizeParam = urlParams.get('pageSize');
  
  state.pagination = {
    page: pageParam ? parseInt(pageParam, 10) : 0,
    pageSize: pageSizeParam ? parseInt(pageSizeParam, 10) : 10
  };
  
  // Ensure valid pagination values
  if (isNaN(state.pagination.page) || state.pagination.page < 0) {
    state.pagination.page = 0;
  }
  if (isNaN(state.pagination.pageSize) || state.pagination.pageSize < 1) {
    state.pagination.pageSize = 10;
  }
  
  // Column visibility deserialization removed as requested
  state.columnVisibility = {};
  
  return state;
};

// Update URL with new state (preserves other URL parameters)
export const updateUrlWithTableState = (navigate, location, tableState, tableId = 'table') => {
  const currentParams = new URLSearchParams(location.search);
  const serializedState = serializeTableState(tableState);
  
  // Remove existing table parameters for this table instance
  const tableParams = ['sort', 'search', 'page', 'pageSize'];
  const prefixedParams = tableId === 'table' 
    ? tableParams 
    : tableParams.map(param => `${tableId}_${param}`);
  
  prefixedParams.forEach(param => currentParams.delete(param));
  
  // Add new table parameters
  Object.entries(serializedState).forEach(([key, value]) => {
    const paramName = tableId === 'table' ? key : `${tableId}_${key}`;
    currentParams.set(paramName, value);
  });
  
  // Update URL without causing page reload
  const newSearch = currentParams.toString();
  const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
  
  // Use replace to avoid creating too many history entries
  navigate(newUrl, { replace: true });
};

// Extract table state from URL for a specific table instance
export const getTableStateFromUrl = (location, tableId = 'table') => {
  const urlParams = new URLSearchParams(location.search);
  
  // Create a new URLSearchParams with just this table's parameters
  const tableParams = new URLSearchParams();
  
  if (tableId === 'table') {
    // Default table uses unprefixed parameters
    ['sort', 'search', 'page', 'pageSize'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        tableParams.set(param, value);
      }
    });
  } else {
    // Named tables use prefixed parameters
    ['sort', 'search', 'page', 'pageSize'].forEach(param => {
      const prefixedParam = `${tableId}_${param}`;
      const value = urlParams.get(prefixedParam);
      if (value) {
        tableParams.set(param, value);
      }
    });
  }
  
  return deserializeTableState(tableParams);
};

// Clean up table parameters from URL
export const cleanTableParamsFromUrl = (navigate, location, tableId = 'table') => {
  const currentParams = new URLSearchParams(location.search);
  
  const tableParams = ['sort', 'search', 'page', 'pageSize'];
  const prefixedParams = tableId === 'table' 
    ? tableParams 
    : tableParams.map(param => `${tableId}_${param}`);
  
  let hasChanges = false;
  prefixedParams.forEach(param => {
    if (currentParams.has(param)) {
      currentParams.delete(param);
      hasChanges = true;
    }
  });
  
  if (hasChanges) {
    const newSearch = currentParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    navigate(newUrl, { replace: true });
  }
};
